import React, { useMemo, useState, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import { fixMarkerOverlaps } from "./graphUtils";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TrendGame {
  game_date: string;
  g_match_id: string | null;
  opponent: string;
  stat_value: number;
  condition_met: boolean;
  increased: boolean | null; // null for the oldest game (nothing to compare to)
}

export interface TrendPlayer {
  player_name: string;
  team_name: string;
  condition_met_count: number;
  total_games: number;
  condition_ratio: number;
  increase_count: number;
  increase_ratio: number;
  dense_rank: number;
  games: TrendGame[];
}

export type TrendMode = "x_of_y" | "threshold" | "directional";

export interface TrendGraphData {
  query_type: string;
  chart_type: string;
  trend_mode: TrendMode;
  threshold_label: string | null;
  labels: string[];
  values: number[];
  players: TrendPlayer[];
  total_records: number;
  sql_query?: string;
}

interface TrendGraphProps {
  data: TrendGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EC4899",
  "#8B5CF6", "#06B6D4", "#F97316", "#14B8A6", "#EF4444", "#84CC16",
];

const GREEN = "#10B981";
const RED = "#EF4444";
const GRAY = "#94a3b8";

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseThresholdValue(label: string | null): number | null {
  if (!label) return null;
  const match = label.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
}

function formatDateShort(dateStr: string): string {
  if (/^\d{4}$/.test(dateStr.trim())) return dateStr; // season year
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

function sortedGames(player: TrendPlayer): TrendGame[] {
  return [...player.games].sort(
    (a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
  );
}

function isConditionMode(mode: TrendMode): boolean {
  return mode === "x_of_y" || mode === "threshold";
}

// ── Styles (scoped under .trg prefix) ─────────────────────────────────────────

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .trg * { box-sizing: border-box; }

  .trg {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .trg::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(16,185,129,0.06) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .trg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .trg-chips-wrapper {
    position: relative;
    z-index: 1;
    margin-bottom: 18px;
  }

  .trg-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border); max-height: 164px; overflow-y: auto;
  }

  .trg-chip-all {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
  }
  .trg-chip-all:hover { background: #f0f9ff; border-color: #93c5fd; color: #2563eb; }
  .trg-chip-all.active { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }

  .trg-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 10px 5px 8px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    border: 1.5px solid transparent;
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .trg-chip:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); color: var(--text-primary); }
  .trg-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .trg-swatch {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .trg-chip.active .trg-swatch { transform: scale(1.4); box-shadow: 0 0 6px var(--chip-color); }

  .trg-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
  }

  .trg-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(0,0,0,0.06);
    color: var(--text-secondary);
    flex-shrink: 0;
    transition: background 0.18s ease, color 0.18s ease;
  }
  .trg-chip.active .trg-chip-stat-badge {
    background: var(--chip-color);
    color: #ffffff;
  }

  .trg-chip-check {
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .trg-chart-wrapper {
    position: relative;
    z-index: 2;
    width: 100%;
    border-radius: 14px;
    overflow-x: auto;
    overflow-y: hidden;
    background: white; padding-top: 26px;
    border: 1px solid var(--border);
  }

  .trg-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important;
    overflow: visible !important;
  }

  .trg-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 8px;
  }

  .trg-empty-icon { font-size: 28px; opacity: 0.35; }

  .trg-mode-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .trg-legend-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 3px 0;
    font-size: 11px;
    color: var(--text-secondary);
  }

  .trg-legend-dot {
    width: 9px; height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const TrendGraph: React.FC<TrendGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players, trend_mode, threshold_label } = data;
  const conditionMode = isConditionMode(trend_mode);

  // Default: all players selected
  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i))
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else if (next.size >= 2) { return prev; } else { next.add(idx); }
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    if (selectedPlayers.size >= Math.min(2, players.length)) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i)));
    }
  }, [players.length, selectedPlayers.size]);

  const selectedIndicesOrdered = useMemo(
    () =>
      Array.from(selectedPlayers)
        .filter((idx) => idx >= 0 && idx < players.length)
        .sort((a, b) => a - b),
    [selectedPlayers, players.length]
  );

  const selectedCount = selectedIndicesOrdered.length;

  // Pre-sort each selected player's games once  // Assign unique colors to selected players (dedupe if same slot)
  const playerColorMap = useMemo(() => {
    const map = new Map<number, string>();
    const used = new Set<number>();
    selectedIndicesOrdered.forEach((playerIdx) => {
      let slot = playerIdx % PALETTE.length;
      while (used.has(slot)) slot = (slot + 1) % PALETTE.length;
      used.add(slot);
      map.set(playerIdx, PALETTE[slot]);
    });
    return map;
  }, [selectedIndicesOrdered]);


  const gameLookup = useMemo<TrendGame[][]>(() => {
    return selectedIndicesOrdered.map((idx) => sortedGames(players[idx]));
  }, [players, selectedIndicesOrdered]);

  const maxGames = useMemo(
    () => Math.max(0, ...gameLookup.map((g) => g.length)),
    [gameLookup]
  );

  // ── x-axis categories ──────────────────────────────────────────────────────
  // Single player → actual dates. Multi-player → G1..Gn
  const xCategories = useMemo(() => {
    if (selectedCount === 1 && gameLookup[0]) {
      return gameLookup[0].map((g) => formatDateShort(g.game_date));
    }
    return Array.from({ length: maxGames }, (_, i) => `G${i + 1}`);
  }, [selectedCount, gameLookup, maxGames]);

  // ── Threshold value (condition modes only) ─────────────────────────────────
  const thresholdValue = useMemo(() => {
    if (!conditionMode) return null;
    return parseThresholdValue(threshold_label);
  }, [conditionMode, threshold_label]);

  // ── Series ─────────────────────────────────────────────────────────────────
  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered.map((playerIdx, seriesIdx) => {
      const player = players[playerIdx];
      if (!player) return { name: "", data: [] };
      const games = gameLookup[seriesIdx];

      if (selectedCount === 1) {
        return {
          name: player.player_name,
          data: games.map((g) => g.stat_value),
        };
      }

      // Multi-player: use {x, y} with explicit category label
      return {
        name: player.player_name,
        data: games.map((g, i) => ({
          x: `G${i + 1}`,
          y: g.stat_value,
        })),
      };
    });
  }, [players, selectedIndicesOrdered, selectedCount, gameLookup]);

  // ── Discrete markers (condition-met or directional coloring) ────────────────
  const discreteMarkers = useMemo(() => {
    const markers: any[] = [];

    selectedIndicesOrdered.forEach((playerIdx, seriesIdx) => {
      const games = gameLookup[seriesIdx];
      if (!games) return;

      games.forEach((game, dataPointIdx) => {
        if (conditionMode) {
          // x_of_y / threshold: green if condition met, red if not
          markers.push({
            seriesIndex: seriesIdx,
            dataPointIndex: dataPointIdx,
            fillColor: game.condition_met ? GREEN : RED,
            strokeColor: "#ffffff",
            size: game.condition_met ? 7 : 6,
          });
        } else {
          // directional: green if increased, red if decreased, gray if null
          const isNull = game.increased == null;
          const increased = game.increased === true;
          markers.push({
            seriesIndex: seriesIdx,
            dataPointIndex: dataPointIdx,
            fillColor: isNull ? GRAY : increased ? GREEN : RED,
            strokeColor: "#ffffff",
            size: isNull ? 5 : 7,
          });
        }
      });
    });

    return markers;
  }, [selectedIndicesOrdered, gameLookup, conditionMode]);

  // ── Threshold annotation (condition modes only) ────────────────────────────
  const thresholdAnnotation = useMemo(() => {
    if (!conditionMode || thresholdValue === null) return undefined;
    return {
      yaxis: [
        {
          y: thresholdValue,
          borderColor: "rgba(16,185,129,0.5)",
          borderWidth: 2,
          strokeDashArray: 6,
          label: {
            text: threshold_label ?? "",
            position: "right" as const,
            style: {
              color: "#065f46",
              background: "#ecfdf5",
              fontSize: "11px",
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: "600",
              borderRadius: "4px",
              borderColor: "#6ee7b7",
              borderWidth: 1,
              padding: { left: 6, right: 6, top: 2, bottom: 2 },
            },
          },
        },
      ],
    };
  }, [conditionMode, thresholdValue, threshold_label]);

  // ── Horizontal scroll: dynamic width per game count ────────────────────────
  const chartWidth = Math.max(700, xCategories.length * 90);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // ── ApexCharts options ─────────────────────────────────────────────────────
  const isSinglePlayer = selectedCount === 1;

  const options: ApexOptions = useMemo((): ApexOptions => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650, easing: "easeinout" },
      height: 380,
      events: {
        mounted: (chartCtx: any) => fixMarkerOverlaps(chartCtx.el),
        updated: (chartCtx: any) => fixMarkerOverlaps(chartCtx.el),
      },
    },
    colors: selectedIndicesOrdered.map((playerIdx) => playerColorMap.get(playerIdx)!),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 6,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 6 },
      discrete: discreteMarkers,
      onClick: (_e: any) => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;
        const point = _e.target;
        const pointX = point.getBoundingClientRect().left;
        const wrapperX = wrapper.getBoundingClientRect().left;
        const offset = pointX - wrapperX;
        wrapper.scrollTo({
          left: wrapper.scrollLeft + offset - wrapper.clientWidth / 2,
          behavior: "smooth",
        });
      },
    },
    xaxis: {
      type: "category" as const,
      categories: xCategories,
      tickPlacement: "on" as const,
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        rotate: isSinglePlayer && xCategories.length > 8 ? -35 : 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      title: {
        text: isSinglePlayer ? "Game Date" : "Game #",
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetY: 6,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => val.toFixed(0),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetX: -4,
      },
    },
    ...(thresholdAnnotation ? { annotations: thresholdAnnotation } : {}),
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 8 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      fixed: { enabled: false },
      followCursor: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        if (!player) return "";
        const games = gameLookup[seriesIndex];
        const game = games?.[dataPointIndex];
        if (!game) return "";
        const color = PALETTE[playerIdx % PALETTE.length];
        return buildTooltip(
          player, game, color, statLabel,
          trend_mode, threshold_label, dataPointIndex
        );
      },
    },
    dataLabels: { enabled: false },
  }), [
    selectedIndicesOrdered, xCategories, statLabel, gameLookup,
    discreteMarkers, thresholdAnnotation, isSinglePlayer,
    players, trend_mode, threshold_label,
  ]);

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="trg">
          <div className="trg-empty" style={{ height: 360 }}>
            <span className="trg-empty-icon">📭</span>
            <span>No trend data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === Math.min(2, players.length);

  return (
    <>
      <style>{css}</style>
      <div className="trg">

        {/* ── Header: mode badge + legend hints ── */}
        <div style={{
          position: "relative", zIndex: 2,
          display: "flex", alignItems: "center", gap: 18,
          marginBottom: 12, flexWrap: "wrap",
        }}>
          <span className="trg-mode-badge" style={{
            background: conditionMode ? "#ecfdf5" : "#eff6ff",
            color: conditionMode ? "#065f46" : "#2563eb",
            border: `1px solid ${conditionMode ? "#6ee7b7" : "#93c5fd"}`,
          }}>
            {trend_mode === "x_of_y" ? "📊 X of Y" :
             trend_mode === "threshold" ? "📈 Threshold" :
             "↗ Directional"}
          </span>

          {conditionMode && (
            <>
              <div className="trg-legend-row">
                <span className="trg-legend-dot" style={{ background: GREEN }} />
                <span>Condition met</span>
              </div>
              <div className="trg-legend-row">
                <span className="trg-legend-dot" style={{ background: RED }} />
                <span>Not met</span>
              </div>
            </>
          )}
          {!conditionMode && (
            <>
              <div className="trg-legend-row">
                <span className="trg-legend-dot" style={{ background: GREEN }} />
                <span>Increased</span>
              </div>
              <div className="trg-legend-row">
                <span className="trg-legend-dot" style={{ background: RED }} />
                <span>Decreased</span>
              </div>
            </>
          )}
        </div>

        {/* ── Player chips ── */}
        <div className="trg-chips-wrapper">
          <div className="trg-chips">
            <button
              className={`trg-chip-all${allSelected ? " active" : ""}`}
              onClick={selectAllPlayers}
            >
              {allSelected ? "✕ Clear" : `⊕ All (up to ${Math.min(2, players.length)})`}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>
                ({players.length})
              </span>
            </button>

            {players.map((player, idx) => {
              const active = selectedPlayers.has(idx);
              const color = playerColorMap.get(idx) ?? "#cbd5e1";

              // Summary badge text depends on mode
              const badgeText = conditionMode
                ? `${player.condition_met_count}/${player.total_games}`
                : `${player.increase_count}/${player.total_games - 1}↑`;

              return (
                <button
                  key={idx}
                  className={`trg-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                  title={`${player.player_name} · ${player.team_name} · ${badgeText}`}
                >
                  <span
                    className="trg-swatch"
                    style={{ background: active ? color : "#cbd5e1" }}
                  />
                  <span className="trg-chip-name">{player.player_name}</span>
                  <span className="trg-chip-stat-badge">{badgeText}</span>
                  {active && (
                    <span className="trg-chip-check" style={{ color }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Chart ── */}
        <div className="trg-chart-wrapper" ref={chartWrapperRef}>
          {series.length > 0 && chartReady ? (
            <ReactApexChart
              type="line"
              series={series}
              options={options}
              height={380}
              width={chartWidth}
            />
          ) : (
            <div className="trg-empty" style={{ height: 380 }}>
              <span className="trg-empty-icon">↑</span>
              <span>Select players above to view trends</span>
            </div>
          )}
        </div>

      </div>
    </>
  );
};

// ── Tooltip builder ───────────────────────────────────────────────────────────

function buildTooltip(
  player: TrendPlayer,
  game: TrendGame,
  color: string,
  statLabel: string,
  mode: TrendMode,
  thresholdLabel: string | null,
  dataPointIndex: number
): string {
  const conditionMode = isConditionMode(mode);
  const displayDate = formatDateShort(game.game_date);

  // Condition row (for x_of_y / threshold)
  const conditionRow = conditionMode
    ? `
      <div style="
        margin-top:8px;
        display:flex;align-items:center;justify-content:space-between;
        background:${game.condition_met ? "#ecfdf5" : "#fef2f2"};
        border-radius:8px;padding:7px 10px;
        border:1px solid ${game.condition_met ? "#6ee7b7" : "#fecaca"};
      ">
        <span style="font-size:10px;font-weight:600;color:${game.condition_met ? "#065f46" : "#991b1b"};">
          ${game.condition_met ? "✓ Condition met" : "✗ Not met"}
        </span>
        <span style="font-size:10px;color:#64748b;">
          ${thresholdLabel ?? ""}
        </span>
      </div>`
    : "";

  // Direction row (for directional) — skip first game (nothing to compare)
  const directionRow = !conditionMode && dataPointIndex > 0
    ? `
      <div style="
        margin-top:8px;
        display:flex;align-items:center;gap:6px;
        background:${game.increased ? "#ecfdf5" : "#fef2f2"};
        border-radius:8px;padding:7px 10px;
        border:1px solid ${game.increased ? "#6ee7b7" : "#fecaca"};
      ">
        <span style="font-size:14px;">${game.increased ? "↗" : "↘"}</span>
        <span style="font-size:10px;font-weight:600;color:${game.increased ? "#065f46" : "#991b1b"};">
          ${game.increased ? "Increased" : "Decreased"} from previous
        </span>
      </div>`
    : "";

  return `
    <div style="
      background:#ffffff;
      border:1px solid #e2e8f0;
      border-top:3px solid ${color};
      border-radius:14px;
      padding:13px 15px;
      font-family:'Plus Jakarta Sans',sans-serif;
      box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
      min-width:205px;max-width:260px;
      position:relative;z-index:9999;
    ">
      <!-- Header -->
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
        <span style="font-weight:700;color:#0f172a;font-size:13px;letter-spacing:-0.01em;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          ${player.player_name}
        </span>
      </div>

      <!-- Date + Opponent -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px;">
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">Date</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${displayDate}</div>
        </div>
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">vs</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${game.opponent || "N/A"}</div>
        </div>
      </div>

      <!-- Stat value -->
      <div style="
        display:flex;align-items:center;justify-content:space-between;
        background:${color}10;border:1px solid ${color}25;
        border-radius:10px;padding:9px 13px;
      ">
        <span style="font-family:'JetBrains Mono',monospace;font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.08em;">
          ${statLabel}
        </span>
        <span style="font-size:22px;font-weight:800;color:${color};letter-spacing:-0.02em;">
          ${game.stat_value}
        </span>
      </div>

      ${conditionRow}
      ${directionRow}
    </div>`;
}

export default TrendGraph;
